/**
 * Offerings / Packages API wrapper (v2 — DNKADM-0102, DNKAPI-0181).
 *
 * A **Package** is an `offerings` row with `kind=package`: a one-time-purchase
 * offering that grants credits once (e.g. "10-Bay Package" → 10 bay-reservation
 * credits, no subscription/renewal). The offering holds the sellable identity
 * (name, price, description, `active`); its inline **`credits`** array is the
 * explicit by-bucket grants ("what's in it") and **`locationIds`** are the owned
 * locations it's available at (`[]` ⇒ ALL — DNKAPI-0254). `termDays`/`provider`/
 * `providerProductId` are always **`null`** for a package (one-time, no PSP
 * product mapping here — selling is dink-journix's separate surface, out of scope).
 *
 * This screen manages **packages only** — every list read sends `?kind=package`
 * and every write sends `kind:'package'`; a package's `kind` is fixed on create
 * and NEVER changed on edit (memberships stay on `/v2/admin/membership-plans`).
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the writes (`isV2AdminSurface`).
 *
 * **Endpoints (DNKAPI-0181 — standard REST + soft-delete + a credit sub-resource):**
 *  - `GET    /v2/admin/offerings?kind=package&status=active|all` → {@link Offering}`[]`.
 *  - `GET    /v2/admin/offerings/{id}` → one {@link Offering}.
 *  - `POST   /v2/admin/offerings` ← {@link OfferingInput} → 201 the created row (409 dup name).
 *  - `PUT    /v2/admin/offerings/{id}` ← {@link OfferingInput} → the updated row.
 *  - `POST   /v2/admin/offerings/{id}/deactivate` | `/reactivate` → the updated row.
 *  - `POST   /v2/admin/offerings/{id}/publish` → take a Draft live (idempotent) → the updated row.
 *  - `POST   /v2/admin/offerings/{id}/copy` ← optional `{name}` → 201 the new Draft clone.
 *  - `DELETE /v2/admin/offerings/{id}` → 204 (soft-remove).
 *  - `GET    /v2/admin/offerings/{id}/credits` → {@link OfferingCredit}`[]`.
 *  - `PUT    /v2/admin/offerings/{id}/credits/{creditBucketId}` ← `{ amount, expiryDays }` → upsert (replaces).
 *  - `DELETE /v2/admin/offerings/{id}/credits/{creditBucketId}` → 204.
 *
 * **Errors** (thrown as the shared typed `ApiError`, `./errors`, by `request()`):
 * `400` VALIDATION (missing/invalid kind, bad price, bad grant amount, kind-flip on
 * edit), `404` NOT_FOUND (foreign/unknown package or bucket id), `409` CONFLICT (dup
 * name per tenant). Callers read the code + `serverMessage(err)` — no
 * separate error type is minted here.
 *
 * Reads are parsed DEFENSIVELY (mirrors `membershipPlans.ts` / `creditBuckets.ts`):
 * a missing/wrong-typed field degrades (an id-less row is dropped), never a throw.
 */
import { request } from './client';
import type { ActiveFilterValue } from '@/hooks/useActiveFilter';

/** v2 admin offerings base path (bearer surface). */
const PATH = '/v2/admin/offerings';

/**
 * The offering `kind` this screen owns. Fixed to `package` on create and never
 * changed on edit (memberships are a different entity, on `/v2/admin/membership-plans`).
 */
const PACKAGE_KIND = 'package' as const;

/**
 * One inline credit grant on a package — the explicit "what's in it" (DNKAPI-0181).
 * Keyed by `creditBucketId` (the 0052 first-class bucket id); `bucketName` is the
 * server-resolved display name; `amount` is the credits granted once on purchase.
 * No grant row for a bucket = that bucket grants nothing (no implicit/unlimited grant).
 *
 * DNKADM-0127 / DNKAPI-0276: a grant now carries `expiryDays` — `0` = never expires
 * (the default; today's behavior), `N` (> 0) = the credits deposited on purchase expire
 * `N` days after the buyer's purchase date. Additive/nullable on the wire: a missing/
 * odd value degrades to `0` (never), mirroring the plan add-on `expiryDays`.
 */
export interface OfferingCredit {
  creditBucketId: string;
  bucketName: string;
  amount: number;
  expiryDays: number;
}

/** The three lifecycle states an offering can be in (DNKAPI-0261). */
type OfferingStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE';

/**
 * Who can buy a package in the member app (DNKAPI-0577 / DNKADM-0412) — the lowercase wire
 * value: `everyone` (the default), `members` (only active members; others see it labeled
 * Members only) or `staff` (front desk only — hidden from the member app). Admin selling and
 * grants never consult it.
 */
export type PurchaseAudience = 'everyone' | 'members' | 'staff';

/**
 * One `offering` row with `kind=package` (DNKAPI-0181). `active` (NOT `isActive`)
 * is the soft-delete flag. `credits` is the inline by-bucket grant array (`[]` =
 * grants nothing). `locationIds` are the owned locations the package is available
 * at — **`[]` means ALL locations** (DNKAPI-0254). `termDays`/`provider`/
 * `providerProductId` are always `null` for a package. `description`/`createdAt`/
 * `updatedAt` are omitted when absent (exactOptionalPropertyTypes).
 */
export interface Offering {
  id: string;
  /** Sellable name (unique on `(tenant, name)`). */
  name: string;
  /** Offering discriminator — `package` for every row this screen manages. */
  kind: string;
  /**
   * Price (DECIMAL(10,2)) — a plain decimal in the tenant currency (NOT cents).
   * `null` when the server sent no price or an unparseable one — an UNPRICED offering,
   * which cannot be sold. A real `0` is preserved and means a genuinely FREE package
   * (given away via a comp order, never sold on a priced cart — see `SellCartDialog`).
   */
  price: number | null;
  /** One-time offering: no term/renewal/expiry on the offering itself. */
  termDays: null;
  /** No PSP product mapping here (selling is dink-journix, out of scope). */
  provider: null;
  providerProductId: null;
  /** Soft-delete flag (DNKAPI-0181); `false` = deactivated. */
  active: boolean;
  /**
   * Lifecycle status (DNKAPI-0261): `DRAFT` (created, never published — hidden from
   * customers, not purchasable), `ACTIVE` (published and live), `INACTIVE` (deactivated/
   * soft-removed). Independent of `active`: a Draft is still `active:true` underneath;
   * `INACTIVE` and `active:false` move together once an offering has been published.
   * Always present on a GET response.
   */
  status: OfferingStatus;
  /** Inline by-bucket grants — the explicit contents (`[]` = grants nothing). */
  credits: OfferingCredit[];
  /** Owned locations this package is available at; `[]` ⇒ ALL locations. */
  locationIds: string[];
  /** Who can buy it in the member app (DNKAPI-0577); `everyone` when absent/unrecognized. */
  purchaseAudience: PurchaseAudience;
  /**
   * Whether a sale of this package is taxed (DNKAPI-0330; DNKADM-0416) — exclusive tax from
   * the member's home location's active rates (DNKAPI-0331), never the sale location's.
   * `false` when absent/unparseable (the server default).
   */
  taxable: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Create/update body for a package (the editable fields). `name` is required by the
 * form; `price` is required-in-practice (blocked on empty even if the backend is
 * lenient); `description` is optional. `locationIds` is always sent by the dialog
 * (`[]` ⇒ ALL locations, else the selected set). `kind` is NOT settable here — the
 * wrappers inject `kind:'package'` + null `termDays`/`provider`/`providerProductId`.
 */
export interface OfferingInput {
  name: string;
  price: number;
  description?: string;
  locationIds?: string[];
  /** Who can buy (DNKAPI-0577); absent leaves the server value unchanged (`everyone` on create). */
  purchaseAudience?: PurchaseAudience;
  /**
   * Charge tax (DNKADM-0416). Absent is omitted from the body: DNKAPI-0585 reads that as
   * "unchanged" on update (the server default on create). A server from before DNKAPI-0585
   * reads an absent `taxable` as `false`.
   */
  taxable?: boolean;
}

// --- Defensive parsing helpers (mirrors membershipPlans.ts) --------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive
 * as numbers on the wire and are normalized to the `string` the UI/router use — a
 * finite number → `String(n)`, a non-empty string → trimmed, else null.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A boolean from a real boolean, or a fallback when absent/unparseable. */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** Parse a raw lifecycle status; anything unrecognized degrades to `ACTIVE` (never crashes). */
function asOfferingStatus(value: unknown): OfferingStatus {
  if (typeof value === 'string') {
    const upper = value.toUpperCase();
    if (upper === 'DRAFT') return 'DRAFT';
    if (upper === 'INACTIVE') return 'INACTIVE';
  }
  return 'ACTIVE';
}

/** Parse a raw purchase audience; anything absent/unrecognized degrades to `everyone`. */
function asPurchaseAudience(value: unknown): PurchaseAudience {
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower === 'members') return 'members';
    if (lower === 'staff') return 'staff';
  }
  return 'everyone';
}

/** A finite number (accepts numeric strings, e.g. a BigDecimal serialized as text), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Parse the inline `locationIds` array off an offering row (DNKAPI-0254): each entry
 * is a v2 entity id (numeric-or-string on the wire), normalized through {@link asId},
 * dropping nulls and duplicates (order-preserving — mirrors `services.ts`'s
 * `extractIds` entry normalization, inline on the row). Absent/non-array/malformed →
 * `[]` (the safe default — "all locations" — never a throw; this is a display field).
 */
function parseLocationIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const ids: string[] = [];
  for (const entry of value) {
    const id = asId(entry);
    if (id !== null && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * Parse one raw inline credit grant, or null when it has no usable `creditBucketId`
 * (a grant that can't be keyed to a bucket can't be rendered — drop it). A missing
 * `bucketName` degrades to an empty string; a missing/negative `amount` degrades to 0.
 */
function parseOfferingCredit(raw: unknown): OfferingCredit | null {
  if (!isObject(raw)) return null;
  const creditBucketId = asId(raw['creditBucketId']);
  if (creditBucketId === null) return null;
  const amount = asNumber(raw['amount']);
  return {
    creditBucketId,
    bucketName: asText(raw['bucketName']) ?? '',
    amount: amount !== null && amount >= 0 ? amount : 0,
    // DNKADM-0127: `expiryDays` floored to a non-negative integer (mirrors
    // membershipTierCredits) — a missing/negative/fractional value degrades to 0 (never).
    expiryDays: Math.max(0, Math.trunc(asNumber(raw['expiryDays']) ?? 0)),
  };
}

/** Tolerantly extract the inline credits array (bare array or `{ credits }`/`{ content }`). */
function extractCredits(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['credits'])) return body['credits'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return [];
}

/**
 * Parse one raw offering, or null when it has no usable `id` (an id-less row can't
 * be rendered or edited — drop it). A missing `name` degrades to an empty string;
 * `kind` degrades to `package` (this screen only ever loads packages). An absent or
 * unparseable `price` stays **`null`** (unpriced) — never `0`, which is a genuinely
 * free package (DNKADM-0245). The wire flag
 * is **`active`** (DNKAPI-0181); a legacy `isActive` is tolerated as a fallback.
 * `locationIds` degrades to `[]` (⇒ all locations) when absent/malformed.
 * `termDays`/`provider`/`providerProductId` are always `null` for a package.
 * `description`/`createdAt`/`updatedAt` are assigned only when present
 * (exactOptionalPropertyTypes).
 */
function parseOffering(raw: unknown): Offering | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;

  const offering: Offering = {
    id,
    name: asText(raw['name']) ?? '',
    kind: asText(raw['kind']) ?? PACKAGE_KIND,
    // An absent/unparseable price stays `null` (UNPRICED) — it is NOT collapsed onto
    // `0`, which is a real, deliberately FREE package (DNKADM-0245).
    price: asNumber(raw['price']),
    termDays: null,
    provider: null,
    providerProductId: null,
    active: asBoolean(raw['active'] ?? raw['isActive'], true),
    status: asOfferingStatus(raw['status']),
    credits: extractCredits(raw['credits'])
      .map(parseOfferingCredit)
      .filter((c): c is OfferingCredit => c !== null),
    locationIds: parseLocationIds(raw['locationIds']),
    purchaseAudience: asPurchaseAudience(raw['purchaseAudience']),
    taxable: asBoolean(raw['taxable'], false),
  };
  const description = asText(raw['description']);
  if (description !== null) offering.description = description;
  const createdAt = asText(raw['createdAt']);
  if (createdAt !== null) offering.createdAt = createdAt;
  const updatedAt = asText(raw['updatedAt']);
  if (updatedAt !== null) offering.updatedAt = updatedAt;
  return offering;
}

/**
 * Extract the offerings array TOLERANTLY from a successful body, returning `null`
 * when NEITHER a bare array NOR a known wrapper key (`offerings`/`content`) yields
 * one. The list read turns `null` into `[]` (a real first-run state).
 */
function extractOfferings(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['offerings'])) return body['offerings'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return null;
}

/**
 * Build the JSON body for a create/update. `kind` is fixed to `package` and
 * `termDays`/`provider`/`providerProductId` are sent explicitly as `null` (a
 * one-time offering with no PSP product). `locationIds` is sent whenever supplied
 * (the dialog always sends the current array — `[]` for "all locations" or the
 * selected set); `description` is omitted when absent. `purchaseAudience` is sent only when
 * supplied — an absent one leaves the stored value unchanged (DNKAPI-0577). `taxable` is
 * likewise sent only when supplied (DNKADM-0416; "unchanged" once DNKAPI-0585 ships).
 */
function toBody(input: OfferingInput): Record<string, unknown> {
  const json: Record<string, unknown> = {
    name: input.name,
    kind: PACKAGE_KIND,
    price: input.price,
    termDays: null,
    provider: null,
    providerProductId: null,
  };
  if (input.locationIds !== undefined) json['locationIds'] = input.locationIds;
  if (input.description !== undefined) json['description'] = input.description;
  if (input.purchaseAudience !== undefined) json['purchaseAudience'] = input.purchaseAudience;
  if (input.taxable !== undefined) json['taxable'] = input.taxable;
  return json;
}

function offeringPath(id: string): string {
  return `${PATH}/${encodeURIComponent(id)}`;
}

function creditsPath(id: string): string {
  return `${offeringPath(id)}/credits`;
}

function creditPath(id: string, creditBucketId: string): string {
  return `${creditsPath(id)}/${encodeURIComponent(creditBucketId)}`;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/offerings?kind=package&status=…` → the tenant's packages,
 * defensively parsed (a bad entry dropped). **Always sends `kind=package`** — this
 * screen never lists memberships. The `status` filter defaults to `active` (live
 * rows only); pass `all` to include deactivated rows. A body that yields no array
 * degrades to `[]` (a real first-run state). Throws a typed `ApiError` on a non-2xx.
 */
export async function listPackages(status: ActiveFilterValue = 'active'): Promise<Offering[]> {
  const body = await request<unknown>('GET', PATH, { query: { kind: PACKAGE_KIND, status } });
  return (extractOfferings(body) ?? [])
    .map(parseOffering)
    .filter((o): o is Offering => o !== null);
}

/**
 * `GET /v2/admin/offerings/{id}` → one offering (defensively parsed). Throws a typed
 * `ApiError` (`404` NOT_FOUND for a foreign/unknown id).
 */
export async function getOffering(id: string): Promise<Offering | null> {
  const body = await request<unknown>('GET', offeringPath(id));
  return parseOffering(body);
}

/**
 * `POST /v2/admin/offerings` → create a package (`kind:'package'` + null term/
 * provider). Returns the re-parsed created row (its `id` is needed to attach grants
 * — create the package FIRST, then PUT its by-bucket grants). Throws a typed
 * `ApiError` (`400` VALIDATION inline-able, `409` CONFLICT on a dup name).
 */
export async function createPackage(input: OfferingInput): Promise<Offering | null> {
  const body = await request<unknown>('POST', PATH, { json: toBody(input) });
  return parseOffering(body);
}

/**
 * `PUT /v2/admin/offerings/{id}` → update a package. Sends `kind:'package'` — NEVER
 * a different kind (a package can't be converted to a membership). Returns the
 * re-parsed updated row. Throws a typed `ApiError` (`409` on a dup name).
 */
export async function updatePackage(id: string, input: OfferingInput): Promise<Offering | null> {
  const body = await request<unknown>('PUT', offeringPath(id), { json: toBody(input) });
  return parseOffering(body);
}

/**
 * `POST /v2/admin/offerings/{id}/deactivate` → soft-deactivate an offering (never a
 * hard delete). Throws a typed `ApiError`.
 */
export async function deactivateOffering(id: string): Promise<void> {
  await request<unknown>('POST', `${offeringPath(id)}/deactivate`);
}

/**
 * `POST /v2/admin/offerings/{id}/reactivate` → reactivate a soft-deactivated
 * offering. Throws a typed `ApiError`.
 */
export async function reactivateOffering(id: string): Promise<void> {
  await request<unknown>('POST', `${offeringPath(id)}/reactivate`);
}

/**
 * `POST /v2/admin/offerings/{id}/publish` → take a Draft offering live. No body.
 * Idempotent — publishing an already-published offering is a no-op that returns its
 * current state. Returns the re-parsed updated row. Throws a typed `ApiError`.
 */
export async function publishOffering(id: string): Promise<Offering | null> {
  const body = await request<unknown>('POST', `${offeringPath(id)}/publish`);
  return parseOffering(body);
}

/**
 * `POST /v2/admin/offerings/{id}/copy` → deep-clone an offering (credit grants + location
 * assignments, preserving `kind`) into a new Draft. `name` is OPTIONAL: omit it (the
 * one-click path) to let the backend auto-generate a unique `"<source> (Copy)"` name — no
 * body is sent; supply `name` to set your own (a colliding supplied name throws `409
 * DUPLICATE_NAME`). Returns the re-parsed NEW offering. Throws a typed `ApiError`.
 */
export async function copyOffering(id: string, name?: string): Promise<Offering | null> {
  const body = await request<unknown>('POST', `${offeringPath(id)}/copy`, {
    ...(name !== undefined ? { json: { name } } : {}),
  });
  return parseOffering(body);
}

/**
 * `DELETE /v2/admin/offerings/{id}` → soft-remove an offering (204). The row is gone
 * from `status=all` afterward (distinct from deactivate, which keeps the row). Throws
 * a typed `ApiError`.
 */
export async function deleteOffering(id: string): Promise<void> {
  await request<unknown>('DELETE', offeringPath(id));
}

// --- Credit grants (explicit; upsert by bucket) --------------------------------

/**
 * `GET /v2/admin/offerings/{id}/credits` → the package's by-bucket grants,
 * defensively parsed (a bad entry dropped). Throws a typed `ApiError`. Refetch after
 * every grant write.
 */
export async function getOfferingCredits(id: string): Promise<OfferingCredit[]> {
  const body = await request<unknown>('GET', creditsPath(id));
  return extractCredits(body)
    .map(parseOfferingCredit)
    .filter((c): c is OfferingCredit => c !== null);
}

/**
 * `PUT /v2/admin/offerings/{id}/credits/{creditBucketId}` ← `{ amount, expiryDays }` →
 * upsert one `(bucket, amount)` grant (the id-in-path upsert **replaces** the amount for
 * that bucket — never a 2nd row). `amount` is positive. `expiryDays` (DNKADM-0127 /
 * DNKAPI-0276, default `0`) is floored to a non-negative integer — `0` = never expires
 * (contract-equivalent to omitting it; a not-yet-deployed backend ignores the additive
 * field), `N` = the deposited credits expire `N` days after purchase. Throws a typed
 * `ApiError` (`400` bad amount, `404` unknown/foreign bucket). Refetch after this resolves.
 */
export async function setOfferingCredit(
  id: string,
  creditBucketId: string,
  amount: number,
  expiryDays = 0,
): Promise<void> {
  await request<unknown>('PUT', creditPath(id, creditBucketId), {
    json: { amount, expiryDays: Math.max(0, Math.trunc(expiryDays)) },
  });
}

/**
 * `DELETE /v2/admin/offerings/{id}/credits/{creditBucketId}` → remove a grant (the
 * package no longer grants that bucket). Throws a typed `ApiError` (`404` NOT_FOUND).
 * Refetch after this resolves.
 */
export async function deleteOfferingCredit(id: string, creditBucketId: string): Promise<void> {
  await request<unknown>('DELETE', creditPath(id, creditBucketId));
}
